import {ascending, max as d3max, min as d3min} from 'd3-array'
import {axisBottom, axisLeft} from 'd3-axis'
import {controller, target} from '@github/catalyst'
import {scaleLinear, scaleTime} from 'd3-scale'
import {area as d3area} from 'd3-shape'
import {fetchPoll} from '@github-ui/fetch-utils'
import formatSymbol from '../d3/format-symbol'
import memoize from '@github/memoize'
import {select} from 'd3-selection'
import {timeFormat} from 'd3-time-format'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Data = any

const cachedData = memoize(fetchData)

async function fetchData(url: string): Promise<Data> {
  const response = await fetchPoll(url, {headers: {accept: 'application/json'}})
  return await response.json()
}

@controller
class CodeFrequencyGraphElement extends HTMLElement {
  @target graph: HTMLElement

  async connectedCallback() {
    const graph = this.graph
    const url = graph.getAttribute('data-url')
    if (!url) {
      return
    }

    // Remove possibly existing cached graph.
    /* eslint-disable-next-line custom-elements/no-dom-traversal-in-connectedcallback */
    for (const el of graph.querySelectorAll('svg.viz')) {
      el.remove()
    }

    graph.classList.add('is-graph-loading')
    graph.classList.remove('is-graph-load-error', 'is-graph-empty')

    try {
      const data = await cachedData(url)
      graph.classList.remove('is-graph-loading')
      if (data != null && data.unusable) {
        graph.classList.add('is-graph-without-usable-data')
      } else if (data != null && data.tooLarge) {
        graph.classList.add('is-graph-too-large')
      } else if (
        (data != null && data.length === 0) ||
        (data != null && data.summary != null && data.summary.total === 0) ||
        (data[0] != null && data[0].weeks != null && data[0].weeks.length === 0)
      ) {
        graph.classList.add('is-graph-empty')
      } else {
        render(graph, data)
      }
    } catch (error) {
      graph.classList.remove('is-graph-loading')

      if (error instanceof ValuesError) {
        graph.classList.add('is-graph-empty')
      } else {
        graph.classList.add('is-graph-load-error')
        throw error
      }
    }
  }
}

class ValuesError extends Error {}

function render(container: HTMLElement, data: number[][]) {
  const width = Math.round(container.getBoundingClientRect().width)
  const height = 500

  const [padt, padr, padb, padl] = [10, 10, 100, 60]

  const formattedData = data
    .map(d => {
      return [new Date(d[0]! * 1000), d[1], d[2]]
    })
    .sort((a, b) => {
      return ascending(a[0], b[0])
    })

  const additions = formattedData.map(d => {
    return [d[0], d[1]]
  })
  const deletions = formattedData.map(d => {
    return [d[0], d[2]]
  })

  const max = d3max(additions, d => {
    return d[1]
  })
  const min = d3min(deletions, d => {
    return d[1]
  })

  if (!max || !min) throw new ValuesError('Graph has no valid max or min values')

  const weekStart = formattedData[0]![0]!
  const weekEnd = formattedData[formattedData.length - 1]![0]!

  const x = scaleTime()
    .domain([weekStart, weekEnd])
    .range([0, width - padl - padr])
  const y = scaleLinear()
    .domain([min as number, max as number])
    .range([height - padb - padt, 0])

  const xAxis = axisBottom(x).tickFormat(d => {
    if (!(d instanceof Date)) {
      throw new Error('invariant')
    }
    if (!(weekStart instanceof Date)) {
      throw new Error('invariant')
    }
    if (!(weekEnd instanceof Date)) {
      throw new Error('invariant')
    }
    if (weekStart.getFullYear() !== weekEnd.getFullYear()) {
      return timeFormat('%B %Y')(d)
    } else {
      return timeFormat('%d %B %Y')(d)
    }
  })
  const yAxis = axisLeft(y)
    .tickPadding(5)
    .tickSize(width)
    .tickFormat(d => {
      if (!(typeof d === 'number')) {
        throw new Error('invariant')
      }
      return formatSymbol(Math.abs(d))
    })

  const area = d3area()
    .x(d => {
      return x(d[0])
    })
    .y0(d => {
      return y(d[1])
    })
    .y1(() => {
      return y(0)
    })

  const vis = select(container)
    .append('svg')
    .attr('viewBox', `0 0 ${width} ${height}`)
    .attr('preserveAspectRatio', 'xMinYMin meet')
    .attr('class', 'viz code-frequency')
    .append('g')
    .attr('transform', `translate(${padl},${padt})`)

  vis
    .append('g')
    .attr('class', 'x axis')
    .attr('transform', `translate(0, ${height - padt - padb})`)
    .call(xAxis)
    .selectAll('.tick text')
    .attr('fill', '#6a737d')
    .style('text-anchor', 'end')
    .attr('dx', '-.8em')
    .attr('dy', '.15em')
    .attr('transform', 'rotate(-45)')

  vis
    .append('g')
    .attr('class', 'y axis')
    .attr('transform', `translate(${width}, 0)`)
    .call(yAxis)
    .selectAll('.tick text')
    .attr('fill', '#6a737d')

  vis
    .selectAll('path.area')
    .data([additions, deletions])
    .enter()
    .append('path')
    .attr('class', (d, i) => {
      if (i === 0) {
        return 'addition'
      } else {
        return 'deletion'
      }
    })

    .attr(
      'd',
      // @ts-expect-error area typings suggest it could be a non-number tuple
      area,
    )
}
